/* eslint-disable react-doctor/no-aria-hidden-on-focusable -- <canvas> without tabIndex is not focusable; aria-hidden is correct for a purely decorative background animation */
'use client';

import { useEffect, useRef } from 'react';

const COLORS = [
  '#ff6b6b', '#ffd93d', '#6bcb77', '#4d96ff', '#c77dff',
  '#f9844a', '#48cae4', '#e9c46a', '#2a9d8f', '#e76f51',
  '#f72585', '#7209b7',
];

const CONFIG = {
  dotCount: 130,
  minRadius: 4,
  maxRadius: 28,
  baseAlpha: 0.78,
  seed: 42,
  animationSpeed: 0.0004,
  amplitudeX: 0.018,
  amplitudeY: 0.015,
  yFactor: 0.7,
};

interface Dot {
  xN: number;
  yN: number;
  r: number;
  color: string;
  phase: number;
}

function seededRng(seed: number): () => number {
  let s = seed;
  return () => {
    s = (s * 1664525 + 1013904223) & 0xffffffff;
    return (s >>> 0) / 0xffffffff;
  };
}

function buildDots(): Dot[] {
  const rng = seededRng(CONFIG.seed);
  const dots: Dot[] = [];
  for (let i = 0; i < CONFIG.dotCount; i++) {
    dots.push({
      xN: rng(),
      yN: rng(),
      r: CONFIG.minRadius + rng() * (CONFIG.maxRadius - CONFIG.minRadius),
      color: COLORS[Math.floor(rng() * COLORS.length)],
      phase: rng() * Math.PI * 2,
    });
  }
  return dots;
}

export const CanvasAnimation: React.FC = () => {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const dpr = window.devicePixelRatio || 1;
    const dots = buildDots();
    let frame = 0;
    let rafId: number;
    let w = 0;
    let h = 0;

    const resize = (): void => {
      w = canvas.width = window.innerWidth * dpr;
      h = canvas.height = window.innerHeight * dpr;
      ctx.scale(dpr, dpr);
    };

    const draw = (): void => {
      if (!ctx || w === 0 || h === 0) return;
      ctx.clearRect(0, 0, w / dpr, h / dpr);
      const t = frame * CONFIG.animationSpeed;
      dots.forEach(d => {
        const x = (d.xN + Math.sin(t + d.phase) * CONFIG.amplitudeX) * (w / dpr);
        const y = (d.yN + Math.cos(t * CONFIG.yFactor + d.phase) * CONFIG.amplitudeY) * (h / dpr);
        ctx.beginPath();
        ctx.arc(x, y, d.r, 0, Math.PI * 2);
        ctx.fillStyle = d.color;
        ctx.globalAlpha = CONFIG.baseAlpha;
        ctx.fill();
      });
      ctx.globalAlpha = 1;
      frame++;
      rafId = requestAnimationFrame(draw);
    };

    const onResize = (): void => {
      cancelAnimationFrame(rafId);
      resize();
      frame = 0;
      rafId = requestAnimationFrame(draw);
    };

    const onVisibility = (): void => {
      if (document.hidden) {
        cancelAnimationFrame(rafId);
      } else {
        rafId = requestAnimationFrame(draw);
      }
    };

    resize();
    rafId = requestAnimationFrame(draw);
    window.addEventListener('resize', onResize);
    document.addEventListener('visibilitychange', onVisibility);

    return () => {
      cancelAnimationFrame(rafId);
      window.removeEventListener('resize', onResize);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      aria-hidden="true"
      className="fixed inset-0 z-0 pointer-events-none"
    />
  );
};
